import { useApiRef } from '@/components/Functional/hooks-core/useApi'
import {
  useStampQuizFetchFnFactory,
  useTanstackSpotFetchFn,
  useTanstackSpotWithLatLonFetchFnFactory,
} from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { is401Error } from '@/components/Functional/lib/xstateUtils'
import type {
  AcquiredRewardData,
  QuizAnswerData,
  SpotDetailData,
  SpotGetResponse,
  StampAcquireResponse,
  StampQuizDetail,
  StampQuizListResponse,
  UserStampAcquireData,
} from '@/schemas/v2'
import type { SpotPhotoFrameResponse } from '@/schemas/v2/spot'
import { useTokenStore } from '@/store/useTokenStore'
import { useQueryClient } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useMemo, useRef } from 'react'
import { assign, fromPromise, setup } from 'xstate'
import type { ErrorActorEvent } from 'xstate'
import { getActiveStamp } from './stampUtils'

// ---------------------------------------------------------------------------
// GPS ユーティリティ
// ---------------------------------------------------------------------------

export const GPS_ACCURACY_THRESHOLD = 80

type GPSResult = {
  lat: number
  lon: number
  accuracy: number
  available: boolean
  permissionDenied: boolean
}

const getGPS = (): Promise<GPSResult> =>
  new Promise<GPSResult>((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ lat: 0, lon: 0, accuracy: Number.POSITIVE_INFINITY, available: false, permissionDenied: false })
      return
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({
          lat: coords.latitude,
          lon: coords.longitude,
          accuracy: coords.accuracy,
          available: true,
          permissionDenied: false,
        }),
      (error) =>
        resolve({
          lat: 0,
          lon: 0,
          accuracy: Number.POSITIVE_INFINITY,
          available: false,
          permissionDenied: error.code === error.PERMISSION_DENIED,
        }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    )
  })

// ---------------------------------------------------------------------------
// Context / Event 型
// ---------------------------------------------------------------------------

type GPSState = {
  lat: number
  lon: number
  accuracy: number
  available: boolean
  loaded: boolean
  permissionDenied: boolean
}

export type StampFlowContext = {
  spotId: number
  isAuthenticated: boolean
  isAuthInitialized: boolean
  spot: SpotDetailData | null
  /** fetchSpot 時点で確定したアクティブスタンプの ID（日跨ぎによる変化を防ぐため固定） */
  activeStampId: number | null
  /** fetchSpot 時点で確定したアクティブスタンプの種別（日跨ぎによる変化を防ぐため固定） */
  activeStampType: SpotDetailData['stamps'][number]['type'] | null
  hasStamp: boolean
  stampIds: number[]
  gps: GPSState
  quizzes: StampQuizDetail[]
  acquiredStamps: UserStampAcquireData[]
  currentStampIndex: number
  acquiredRewards: AcquiredRewardData[]
  errorMessage: string | null
  pendingChoiceId: number | null
  pendingTextAnswer: string | null
  quizAnswerResult: QuizAnswerData | null
  quizDailyLimitExhausted: boolean
  qrCode: string | null
  ocrCapturedImage: string | null
  ocrFailureId: number | null
  /** スタンプ獲得によって ARカメラが利用可能になったことを通知するフラグ */
  showArPhotoUnlocked: boolean
  /** ページ入場時の POST /photo-frames 結果がスタンプ必要（stamp_required）だったか */
  photoFrameInitiallyStampRequired: boolean
}

type StampFlowEvent =
  | { type: 'AUTH_READY'; isAuthenticated: boolean }
  | { type: 'START_STAMP_FLOW' }
  | { type: 'NOT_AUTH_ERROR_CLOSE' }
  | { type: 'POPUP_CLOSE' }
  | { type: 'QUIZ_CLOSE' }
  | { type: 'ANSWER_SUBMIT'; choiceId: number }
  | { type: 'TEXT_ANSWER_SUBMIT'; answer: string }
  | { type: 'QUIZ_RETRY' }
  | { type: 'STAMP_GET' }
  | { type: 'STAMP_NEXT' }
  | { type: 'QR_READ'; code: string }
  | { type: 'QR_CLOSE' }
  | { type: 'BARCODE_READ'; code: string }
  | { type: 'BARCODE_CLOSE' }
  | { type: 'REWARD_CLOSE' }
  | { type: 'PHOTO_CLOSE' }
  | { type: 'STAMP_REDISPLAY' }
  | { type: 'STAMP_REDISPLAY_POPUP_CLOSE' }
  | { type: 'STAMP_REDISPLAY_STAMP_NEXT' }
  | { type: 'ACQUIRE_ERROR_CLOSE' }
  | { type: 'GPS_RANGE_ERROR_CLOSE' }
  | { type: 'GPS_RANGE_ERROR_RETRY' }
  | { type: 'RESET' }
  | { type: 'OCR_WARNING_CONFIRM' }
  | { type: 'OCR_WARNING_CLOSE' }
  | { type: 'OCR_CAPTURED'; image: string }
  | { type: 'OCR_CAPTURE_CLOSE' }
  | { type: 'OCR_FAILURE_RETRY' }
  | { type: 'OCR_FAILURE_APPLY' }
  | { type: 'OCR_FAILURE_CLOSE' }
  | { type: 'QR_WRONG_FORMAT' }
  | { type: 'QR_WRONG_FORMAT_CLOSE' }
  | { type: 'QUIZ_DAILY_LIMIT_CLOSE' }
  /** トークンリフレッシュ完了後に spotFetching からやり直す（401 エラー時） */
  | { type: 'REFRESH_COMPLETE' }
  | { type: 'AR_PHOTO_UNLOCKED_CLOSE' }

// Actor input 型
type QuizInput = { stampId: number }
type QuizAnswerInput = { stampId: number; quizId: number; choiceId: number | null; textAnswer: string | null }
type AcquireInput = {
  stampId: number
  lat: number
  lon: number
  spotId: number
  qrCode: string | null
  ocrImage: string | null
  gpsAvailable: boolean
  stampType: SpotDetailData['stamps'][number]['type'] | undefined
}
type SpotWithGpsInput = { spotId: number; lat: number; lon: number }
type FetchPhotoFramesInput = { spotId: number; isAuthenticated: boolean }

// ---------------------------------------------------------------------------
// ベースマシン
// ---------------------------------------------------------------------------

const stampFlowMachineBase = setup({
  types: {
    context: {} as StampFlowContext,
    events: {} as StampFlowEvent,
    input: {} as {
      spotId: number
      isAuthenticated: boolean
      isAuthInitialized: boolean
    },
  },
  actors: {
    fetchSpot: fromPromise<SpotGetResponse | undefined, void>(async () => undefined),
    fetchGPS: fromPromise<GPSResult, void>(async () => ({
      lat: 0,
      lon: 0,
      accuracy: Number.POSITIVE_INFINITY,
      available: false,
      permissionDenied: false,
    })),
    fetchQuiz: fromPromise<StampQuizListResponse | undefined, QuizInput>(async () => undefined),
    answerQuiz: fromPromise<{ quiz: QuizAnswerData }, QuizAnswerInput>(async () => ({
      quiz: {
        quizId: 0,
        isCorrect: false,
        answer: null,
        message: null,
        hintMessage: null,
        correctImage: null,
        incorrectImage: null,
        retry: true,
      },
    })),
    acquireStamp: fromPromise<StampAcquireResponse, AcquireInput>(async () => ({ stamps: [], acquiredRewards: [] })),
    fetchSpotWithGps: fromPromise<SpotGetResponse | undefined, SpotWithGpsInput>(async () => undefined),
    fetchUserStamps: fromPromise<boolean, { stampIds: number[] }>(async () => false),
    fetchPhotoFrames: fromPromise<SpotPhotoFrameResponse, FetchPhotoFramesInput>(async () => ({ photoFrames: [] })),
  },
  guards: {
    isNotAuthenticated: ({ context }) => !context.isAuthenticated,
    is401Error: ({ event }) => is401Error((event as unknown as ErrorActorEvent).error),
    hasPopup: () => false,
    hasQuiz: ({ context }) => context.quizzes.length > 0,
    isDescriptiveQuiz: ({ context }) => context.quizzes[0]?.quizType === 1,
    isQrType: ({ context }) => context.activeStampType === 'QR_CODE',
    isBarcodeType: ({ context }) => context.activeStampType === 'BARCODE',
    isOcrType: ({ context }) => context.activeStampType === 'OCR',
    hasOcrWarning: ({ context }) => getActiveStamp(context.spot?.stamps ?? [])?.ocrWarning?.enabled === true,
    hasPhotoUpload: () => false,
    hasMoreStamps: ({ context }) => context.currentStampIndex < context.acquiredStamps.length - 1,
    noStampId: ({ context }) => context.activeStampId === null,
    hasRewards: ({ context }) => context.acquiredRewards.length > 0,
    outOfRange: ({ context }) => context.spot?.isInRange === false,
    canRetryQuiz: ({ context }) => context.quizAnswerResult?.retry !== false,
    isQuizDailyLimitExhausted: ({ context }) => context.quizDailyLimitExhausted,
  },
  actions: {
    clearScanContext: assign({ qrCode: () => null, ocrCapturedImage: () => null }),
    clearArPhotoUnlocked: assign({ showArPhotoUnlocked: () => false }),
  },
}).createMachine({
  id: 'stampFlow',
  context: ({ input }) => ({
    spotId: input.spotId,
    isAuthenticated: input.isAuthenticated,
    isAuthInitialized: input.isAuthInitialized,
    spot: null,
    activeStampId: null,
    activeStampType: null,
    hasStamp: false,
    stampIds: [],
    gps: {
      lat: 0,
      lon: 0,
      accuracy: Number.POSITIVE_INFINITY,
      available: false,
      loaded: false,
      permissionDenied: false,
    },
    quizzes: [],
    acquiredStamps: [],
    currentStampIndex: 0,
    acquiredRewards: [],
    errorMessage: null,
    pendingChoiceId: null,
    pendingTextAnswer: null,
    quizAnswerResult: null,
    quizDailyLimitExhausted: false,
    qrCode: null,
    ocrCapturedImage: null,
    ocrFailureId: null,
    showArPhotoUnlocked: false,
    photoFrameInitiallyStampRequired: false,
  }),
  initial: 'initialLoading',
  // AR_PHOTO_UNLOCKED_CLOSE はどのステートからでも閉じられるようにルートレベルで処理
  on: {
    AR_PHOTO_UNLOCKED_CLOSE: { actions: 'clearArPhotoUnlocked' },
  },
  states: {
    // ---------------------------------------------------------------
    // 初期ロード
    // ---------------------------------------------------------------
    initialLoading: {
      initial: 'waitingForAuth',
      states: {
        // 認証状態が確定するまで待機する。
        // getState() で既に確定済みなら always で即スキップ。
        waitingForAuth: {
          always: [{ guard: ({ context }) => context.isAuthInitialized, target: 'spotFetching' }],
          on: {
            AUTH_READY: {
              target: 'spotFetching',
              actions: assign({
                isAuthenticated: ({ event }) => event.isAuthenticated,
                isAuthInitialized: () => true,
              }),
            },
          },
        },
        spotFetching: {
          invoke: {
            src: 'fetchSpot',
            onDone: {
              target: 'prefetching',
              actions: assign({
                spot: ({ event }) => event.output?.spot ?? null,
                activeStampId: ({ event }) => getActiveStamp(event.output?.spot?.stamps ?? [])?.id ?? null,
                activeStampType: ({ event }) => getActiveStamp(event.output?.spot?.stamps ?? [])?.type ?? null,
                stampIds: ({ event }) => {
                  const stamps = event.output?.spot?.stamps ?? []
                  const active = getActiveStamp(stamps)
                  return active != null ? [active.id] : stamps.map((s) => s.id)
                },
              }),
            },
            onError: [{ guard: 'is401Error', target: '#stampFlow.waitingForRefresh' }, { target: '#stampFlow.error' }],
          },
        },
        prefetching: {
          type: 'parallel' as const,
          states: {
            quiz: {
              initial: 'fetching',
              states: {
                fetching: {
                  always: [{ guard: 'noStampId', target: 'done' }],
                  invoke: {
                    src: 'fetchQuiz',
                    input: ({ context }): QuizInput => ({
                      stampId: context.activeStampId as number,
                    }),
                    onDone: {
                      target: 'done',
                      actions: assign({
                        quizzes: ({ event }) => event.output?.quizzes ?? [],
                      }),
                    },
                    onError: { target: 'done' },
                  },
                },
                done: { type: 'final' as const },
              },
            },
            gps: {
              initial: 'fetching',
              states: {
                fetching: {
                  invoke: {
                    src: 'fetchGPS',
                    onDone: {
                      target: 'done',
                      actions: assign({ gps: ({ event }) => ({ ...event.output, loaded: true }) }),
                    },
                    onError: {
                      target: 'done',
                      actions: assign({
                        gps: () => ({
                          lat: 0,
                          lon: 0,
                          accuracy: Number.POSITIVE_INFINITY,
                          available: false,
                          loaded: true,
                          permissionDenied: false,
                        }),
                      }),
                    },
                  },
                },
                done: { type: 'final' as const },
              },
            },
            userStampCheck: {
              initial: 'checking',
              states: {
                checking: {
                  always: [
                    { guard: 'isNotAuthenticated', target: 'done' },
                    { guard: 'noStampId', target: 'done' },
                  ],
                  invoke: {
                    src: 'fetchUserStamps',
                    input: ({ context }) => ({ stampIds: context.stampIds }),
                    onDone: {
                      target: 'done',
                      actions: assign({
                        hasStamp: ({ event }) => event.output,
                      }),
                    },
                    onError: { target: 'done' },
                  },
                },
                done: { type: 'final' as const },
              },
            },
            photoFrameCheck: {
              initial: 'checking',
              states: {
                checking: {
                  always: [{ guard: ({ context }) => !context.spot?.photoFrame, target: 'done' }],
                  invoke: {
                    src: 'fetchPhotoFrames',
                    input: ({ context }): FetchPhotoFramesInput => ({
                      spotId: context.spotId,
                      isAuthenticated: context.isAuthenticated,
                    }),
                    onDone: {
                      target: 'done',
                      actions: assign({
                        photoFrameInitiallyStampRequired: ({ event }) => {
                          const frames = event.output.photoFrames
                          return (
                            frames.length > 0 &&
                            frames.every((e) => e.status !== 'available') &&
                            frames.some((e) => e.status === 'stamp_required')
                          )
                        },
                      }),
                    },
                    onError: { target: 'done' },
                  },
                },
                done: { type: 'final' as const },
              },
            },
          },
          onDone: { target: 'spotGpsFetching' },
        },
        spotGpsFetching: {
          always: [{ guard: ({ context }) => !context.gps.available, target: '#stampFlow.idle' }],
          invoke: {
            src: 'fetchSpotWithGps',
            input: ({ context }): SpotWithGpsInput => ({
              spotId: context.spotId,
              lat: context.gps.lat,
              lon: context.gps.lon,
            }),
            onDone: {
              target: '#stampFlow.idle',
              actions: assign({
                spot: ({ event }) => event.output?.spot ?? null,
              }),
            },
            onError: { target: '#stampFlow.idle' },
          },
        },
      },
    },
    // ---------------------------------------------------------------
    // Idle
    // ---------------------------------------------------------------
    idle: {
      on: {
        START_STAMP_FLOW: [
          { guard: 'isNotAuthenticated', target: 'notAuthenticatedError', actions: 'clearArPhotoUnlocked' },
          { target: 'gpsRefreshing', actions: 'clearArPhotoUnlocked' },
        ],
        STAMP_REDISPLAY: { target: 'redisplayPopupCheck', actions: 'clearArPhotoUnlocked' },
      },
    },
    // ---------------------------------------------------------------
    // GPS 再取得（スタンプ獲得ボタン押下時）
    // ---------------------------------------------------------------
    gpsRefreshing: {
      invoke: {
        src: 'fetchGPS',
        onDone: {
          target: 'popupCheck',
          actions: assign({ gps: ({ event }) => ({ ...event.output, loaded: true }) }),
        },
        onError: {
          target: 'popupCheck',
          actions: assign({ gps: ({ context }) => ({ ...context.gps, loaded: true }) }),
        },
      },
    },
    // ---------------------------------------------------------------
    // スタンプ獲得フロー
    // ---------------------------------------------------------------
    popupCheck: {
      always: [
        { guard: 'noStampId', target: 'idle' },
        { guard: 'outOfRange', target: 'gpsRangeError' },
        { guard: 'hasPopup', target: 'popupShown' },
        { target: 'qrCheck' },
      ],
    },
    popupShown: {
      on: { POPUP_CLOSE: 'qrCheck' },
    },
    quizCheck: {
      always: [
        { guard: 'isQuizDailyLimitExhausted', target: 'quizDailyLimitError' },
        { guard: 'isDescriptiveQuiz', target: 'descriptiveQuizInput' },
        { guard: 'hasQuiz', target: 'quizChoice' },
        { target: 'acquiring' },
      ],
    },
    descriptiveQuizInput: {
      on: {
        TEXT_ANSWER_SUBMIT: {
          target: 'quizAnswering',
          actions: assign({
            pendingTextAnswer: ({ event }) => event.answer,
            pendingChoiceId: () => null,
          }),
        },
        QUIZ_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    quizChoice: {
      on: {
        ANSWER_SUBMIT: {
          target: 'quizAnswering',
          actions: assign({
            pendingChoiceId: ({ event }) => event.choiceId,
            pendingTextAnswer: () => null,
          }),
        },
        QUIZ_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    quizAnswering: {
      invoke: {
        src: 'answerQuiz',
        input: ({ context }): QuizAnswerInput => ({
          stampId: context.activeStampId as number,
          quizId: context.quizzes[0]?.id as number,
          choiceId: context.pendingChoiceId,
          textAnswer: context.pendingTextAnswer,
        }),
        onDone: [
          {
            guard: ({ event }) => event.output.quiz.isCorrect,
            target: 'quizCorrect',
            actions: assign({ quizAnswerResult: ({ event }) => event.output.quiz }),
          },
          {
            target: 'quizIncorrect',
            actions: assign({
              quizAnswerResult: ({ event }) => event.output.quiz,
              quizDailyLimitExhausted: ({ event }) => event.output.quiz.retry === false,
            }),
          },
        ],
        onError: [{ guard: 'isDescriptiveQuiz', target: 'descriptiveQuizInput' }, { target: 'quizChoice' }],
      },
    },
    quizCorrect: {
      on: {
        STAMP_GET: 'acquiring',
        QUIZ_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    quizIncorrect: {
      on: {
        QUIZ_RETRY: {
          guard: 'canRetryQuiz',
          target: 'quizCheck',
        },
        QUIZ_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    quizDailyLimitError: {
      on: {
        QUIZ_DAILY_LIMIT_CLOSE: {
          target: 'idle',
          actions: 'clearScanContext',
        },
      },
    },
    qrCheck: {
      always: [
        { guard: 'isQuizDailyLimitExhausted', target: 'quizDailyLimitError' },
        { guard: 'isQrType', target: 'qrScan' },
        { guard: 'isBarcodeType', target: 'barcodeScan' },
        { guard: 'isOcrType', target: 'ocrWarningCheck' },
        { target: 'quizCheck' },
      ],
    },
    qrScan: {
      on: {
        QR_READ: {
          target: 'quizCheck',
          actions: assign({
            qrCode: ({ event }) => event.code,
          }),
        },
        QR_CLOSE: {
          target: 'idle',
          actions: assign({ qrCode: () => null }),
        },
        QR_WRONG_FORMAT: {
          target: 'qrWrongFormat',
        },
      },
    },
    barcodeScan: {
      on: {
        BARCODE_READ: {
          target: 'quizCheck',
          actions: assign({
            qrCode: ({ event }) => event.code,
          }),
        },
        BARCODE_CLOSE: {
          target: 'idle',
          actions: assign({ qrCode: () => null }),
        },
      },
    },
    // ---------------------------------------------------------------
    // OCR フロー
    // ---------------------------------------------------------------
    ocrWarningCheck: {
      always: [{ guard: 'hasOcrWarning', target: 'ocrWarning' }, { target: 'ocrCapture' }],
    },
    ocrWarning: {
      on: {
        OCR_WARNING_CONFIRM: 'ocrCapture',
        OCR_WARNING_CLOSE: 'idle',
      },
    },
    ocrCapture: {
      on: {
        OCR_CAPTURED: {
          target: 'quizCheck',
          actions: assign({
            ocrCapturedImage: ({ event }) => event.image,
          }),
        },
        OCR_CAPTURE_CLOSE: 'idle',
      },
    },
    acquiring: {
      invoke: {
        src: 'acquireStamp',
        input: ({ context }): AcquireInput => ({
          stampId: context.activeStampId as number,
          lat: context.gps.lat,
          lon: context.gps.lon,
          gpsAvailable: context.gps.available,
          spotId: context.spotId,
          qrCode: context.qrCode,
          ocrImage: context.ocrCapturedImage,
          stampType: context.activeStampType ?? undefined,
        }),
        onDone: {
          target: 'stampShowing',
          actions: assign({
            acquiredStamps: ({ event }) => event.output.stamps,
            acquiredRewards: ({ event }) => event.output.acquiredRewards,
            currentStampIndex: 0,
            hasStamp: () => true,
            qrCode: () => null,
            ocrCapturedImage: () => null,
          }),
        },
        onError: [
          {
            guard: ({ event }) => {
              const err = event.error as { name?: string } | null
              return err?.name === 'OcrStampError'
            },
            target: 'ocrReadFailure',
            actions: assign({
              ocrFailureId: ({ event }) => {
                const err = event.error as { ocrId?: number } | null
                return err?.ocrId ?? null
              },
            }),
          },
          {
            guard: ({ event }) => {
              const err = event.error as { code?: string } | null
              return err?.code === 'STAMP_CODE_MISMATCH'
            },
            target: 'qrWrongFormat',
          },
          {
            target: 'acquireError',
            actions: assign({
              errorMessage: ({ event }) => {
                const err = event.error as { code?: string } | null
                return err?.code ?? 'error'
              },
            }),
          },
        ],
      },
    },
    ocrReadFailure: {
      on: {
        OCR_FAILURE_RETRY: {
          target: 'ocrCapture',
          actions: assign({ ocrFailureId: () => null, ocrCapturedImage: () => null }),
        },
        OCR_FAILURE_APPLY: {
          target: 'idle',
          actions: assign({ ocrFailureId: () => null, ocrCapturedImage: () => null }),
        },
        OCR_FAILURE_CLOSE: {
          target: 'idle',
          actions: assign({ ocrFailureId: () => null, ocrCapturedImage: () => null }),
        },
      },
    },
    acquireError: {
      on: {
        ACQUIRE_ERROR_CLOSE: {
          target: 'idle',
          actions: assign({ errorMessage: null }),
        },
      },
    },
    qrWrongFormat: {
      on: {
        QR_WRONG_FORMAT_CLOSE: {
          target: 'idle',
        },
      },
    },
    gpsRangeError: {
      on: {
        GPS_RANGE_ERROR_CLOSE: 'idle',
        GPS_RANGE_ERROR_RETRY: 'gpsRefreshing',
      },
    },
    notAuthenticatedError: {
      on: {
        NOT_AUTH_ERROR_CLOSE: 'idle',
      },
    },
    stampShowing: {
      on: {
        STAMP_NEXT: [
          {
            guard: 'hasMoreStamps',
            actions: assign({
              currentStampIndex: ({ context }) => context.currentStampIndex + 1,
            }),
          },
          {
            target: 'rewardCheck',
            actions: assign({
              showArPhotoUnlocked: ({ context }) => context.photoFrameInitiallyStampRequired,
            }),
          },
        ],
      },
    },
    rewardCheck: {
      always: [{ guard: 'hasRewards', target: 'rewardShowing' }, { target: 'photoCheck' }],
    },
    rewardShowing: {
      on: {
        REWARD_CLOSE: 'photoCheck',
      },
    },
    photoCheck: {
      always: [{ guard: 'hasPhotoUpload', target: 'photoPost' }, { target: 'idle' }],
    },
    photoPost: {
      on: {
        PHOTO_CLOSE: 'idle',
      },
    },
    // ---------------------------------------------------------------
    // 再表示フロー
    // ---------------------------------------------------------------
    redisplayPopupCheck: {
      always: [{ guard: 'hasPopup', target: 'redisplayPopupShown' }, { target: 'redisplayStampShowing' }],
    },
    redisplayPopupShown: {
      on: { STAMP_REDISPLAY_POPUP_CLOSE: 'redisplayStampShowing' },
    },
    redisplayStampShowing: {
      on: {
        STAMP_REDISPLAY_STAMP_NEXT: [
          {
            guard: 'hasMoreStamps',
            actions: assign({
              currentStampIndex: ({ context }) => context.currentStampIndex + 1,
            }),
          },
          { target: 'idle' },
        ],
      },
    },
    // ---------------------------------------------------------------
    // エラー
    // ---------------------------------------------------------------
    error: {
      on: { RESET: 'idle' },
    },

    // ---------------------------------------------------------------
    // 401 エラー時のトークンリフレッシュ待機
    // ---------------------------------------------------------------
    /**
     * 401 エラー発生時のトークンリフレッシュ待機状態。
     * AppMachineContext の tokenRefresh が waiting になったら REFRESH_COMPLETE を受信し spotFetching へ遷移する。
     * 30 秒以内に REFRESH_COMPLETE が届かない場合は error ステートへタイムアウトする。
     */
    waitingForRefresh: {
      after: { 10000: '#stampFlow.error' },
      on: { REFRESH_COMPLETE: '#stampFlow.initialLoading.spotFetching' },
    },
  },
})

// ---------------------------------------------------------------------------
// 公開フック
// ---------------------------------------------------------------------------

type StampFlowMachineOptions = {
  isBarCodeWithGPS: boolean
  isQRCodeWithGPS: boolean
  isKeywordWithGPS: boolean
}

export const useStampFlowStateMachine = (spotId: number, options: StampFlowMachineOptions) => {
  const { isBarCodeWithGPS, isQRCodeWithGPS, isKeywordWithGPS } = options
  const queryClient = useQueryClient()
  const spotFetch = useTanstackSpotFetchFn(spotId)
  const quizFetchFactory = useStampQuizFetchFnFactory()
  const spotWithLatLonFetchFactory = useTanstackSpotWithLatLonFetchFnFactory()
  const apiRef = useApiRef()

  // Refs to capture latest values and prevent stale closures in actors
  const queryClientRef = useRef(queryClient)
  const spotFetchRef = useRef(spotFetch)
  const quizFetchFactoryRef = useRef(quizFetchFactory)
  const spotWithLatLonFetchFactoryRef = useRef(spotWithLatLonFetchFactory)
  queryClientRef.current = queryClient
  spotFetchRef.current = spotFetch
  quizFetchFactoryRef.current = quizFetchFactory
  spotWithLatLonFetchFactoryRef.current = spotWithLatLonFetchFactory

  const isQRCodeWithGPSRef = useRef(isQRCodeWithGPS)
  const isBarCodeWithGPSRef = useRef(isBarCodeWithGPS)
  const isKeywordWithGPSRef = useRef(isKeywordWithGPS)
  isQRCodeWithGPSRef.current = isQRCodeWithGPS
  isBarCodeWithGPSRef.current = isBarCodeWithGPS
  isKeywordWithGPSRef.current = isKeywordWithGPS

  // biome-ignore lint/correctness/useExhaustiveDependencies: actors use refs to access latest values; machine is recreated on spotId change via key={id} in pages/[campaign]/spots/detail.tsx, not by useMemo alone (XState v5 useIdleActorRef compares logic.config by reference, and provide() does not change config reference)
  const machine = useMemo(
    () =>
      stampFlowMachineBase.provide({
        actors: {
          fetchSpot: fromPromise(async () => queryClientRef.current.fetchQuery(spotFetchRef.current)),

          fetchGPS: fromPromise<GPSResult, void>(async () => getGPS()),

          fetchQuiz: fromPromise(async ({ input }: { input: QuizInput }) =>
            queryClientRef.current.fetchQuery(quizFetchFactoryRef.current(input.stampId)),
          ),

          fetchSpotWithGps: fromPromise(async ({ input }: { input: SpotWithGpsInput }) =>
            queryClientRef.current.fetchQuery(
              spotWithLatLonFetchFactoryRef.current(input.spotId, input.lat, input.lon),
            ),
          ),

          fetchUserStamps: fromPromise(async ({ input }: { input: { stampIds: number[] } }) => {
            const results = await Promise.all(input.stampIds.map((id) => apiRef.current.auth.fetchUserStamp(id)))
            return results.some((r) => r?.stamp?.isAcquired === true)
          }),

          fetchPhotoFrames: fromPromise(async ({ input }: { input: FetchPhotoFramesInput }) => {
            if (input.isAuthenticated) {
              return await apiRef.current.auth.postSpotPhotoFrames(input.spotId, {})
            }
            return await apiRef.current.guest.postSpotPhotoFrames(input.spotId, {})
          }),

          answerQuiz: fromPromise(async ({ input }: { input: QuizAnswerInput }) => {
            const body: Record<string, unknown> = {}
            if (input.choiceId !== null) body.answerId = input.choiceId
            else if (input.textAnswer !== null) body.answer = input.textAnswer
            return await apiRef.current.auth.postStampQuizAnswer(input.stampId, input.quizId, body)
          }),

          acquireStamp: fromPromise(async ({ input }: { input: AcquireInput }) => {
            const body: Record<string, unknown> = {
              spotId: input.spotId,
            }

            switch (input.stampType) {
              case 'QR_CODE':
                body.code = input.qrCode
                if (isQRCodeWithGPSRef.current) {
                  body.lat = input.lat
                  body.lon = input.lon
                }
                break
              case 'BARCODE':
                body.code = input.qrCode
                if (isBarCodeWithGPSRef.current) {
                  body.lat = input.lat
                  body.lon = input.lon
                }
                break
              case 'GPS':
                body.lat = input.lat
                body.lon = input.lon
                break
              case 'KEYWORD':
                body.keyword = true
                if (isKeywordWithGPSRef.current && input.gpsAvailable) {
                  body.lat = input.lat
                  body.lon = input.lon
                }
                break
              case 'OCR':
                if (input.ocrImage !== null) {
                  // DataURL 形式の場合は純粋 Base64 部分のみ抽出
                  body.image = input.ocrImage.includes(',') ? input.ocrImage.split(',')[1] : input.ocrImage
                }
                break
              default:
                break
            }

            if (input.stampType === 'OCR') {
              return await apiRef.current.auth.postOcrStamp(input.stampId, body)
            }
            return await apiRef.current.auth.postStamp(input.stampId, body)
          }),
        },
      }),
    [spotId],
  )

  const [state, send] = useMachine(machine, {
    input: (() => {
      // Zustand の同期 getState() でマシン生成時点の認証状態を取得。
      // loading が false なら waitingForAuth を即スキップして spotFetching へ進む。
      // まだ loading 中なら waitingForAuth で AUTH_READY イベントを待つ。
      const tokenState = useTokenStore.getState()
      return {
        spotId,
        isAuthenticated: !tokenState.loading && tokenState.idToken !== null,
        isAuthInitialized: !tokenState.loading,
      }
    })(),
  })

  return {
    state,
    send,
    isLoading: state.matches('initialLoading') || state.matches('waitingForRefresh'),
    isError: state.matches('error'),
    isIdle: state.matches('idle'),
    isGpsRefreshing: state.matches('gpsRefreshing'),
    isPopupShown: state.matches('popupShown'),
    isDescriptiveQuizInput: state.matches('descriptiveQuizInput'),
    isQuizChoice: state.matches('quizChoice'),
    isQuizAnswering: state.matches('quizAnswering'),
    isQuizCorrect: state.matches('quizCorrect'),
    isQuizIncorrect: state.matches('quizIncorrect'),
    isQuizDailyLimitError: state.matches('quizDailyLimitError'),
    isQrScan: state.matches('qrScan'),
    isBarcodeScan: state.matches('barcodeScan'),
    isOcrWarning: state.matches('ocrWarning'),
    isOcrCapture: state.matches('ocrCapture'),
    isOcrReadFailure: state.matches('ocrReadFailure'),
    isAcquiring: state.matches('acquiring'),
    isAcquireError: state.matches('acquireError'),
    isQrWrongFormat: state.matches('qrWrongFormat'),
    isGpsRangeError: state.matches('gpsRangeError'),
    isNotAuthenticatedError: state.matches('notAuthenticatedError'),
    isStampShowing: state.matches('stampShowing'),
    isRewardShowing: state.matches('rewardShowing'),
    isPhotoPost: state.matches('photoPost'),
    isArPhotoUnlockedShowing: state.context.showArPhotoUnlocked,
    isRedisplayStampShowing: state.matches('redisplayStampShowing'),
    isRedisplayPopupShown: state.matches('redisplayPopupShown'),
    context: state.context,
    gpsLoaded: state.context.gps.loaded,
    gpsInvalid: state.context.gps.loaded && state.context.gps.accuracy > GPS_ACCURACY_THRESHOLD,
    quizAnswerResult: state.context.quizAnswerResult,
    canRetryQuiz: state.context.quizAnswerResult?.retry !== false,
    ocrFailureId: state.context.ocrFailureId,
  }
}
